import type { Action } from "@core/features/actions";
import { ACTION_TYPE_SLUG } from "@core/features/actions";

/** Org admin onboarding step slugs — used to detect org admin flows */
const ORG_ONBOARDING_SLUGS: string[] = [
  ACTION_TYPE_SLUG.ORG_DISPLAY_NAME,
  ACTION_TYPE_SLUG.ORG_ONBOARDING_QUIZ,
  ACTION_TYPE_SLUG.ORG_UPLOAD_LOGO,
  ACTION_TYPE_SLUG.ORG_UPDATE_SOCIALS,
];

type OnboardingStepDef = { slug: string; sequence: number };

/**
 * Build the correct onboarding URL from an onboarding action.
 * Inspects display_options.onboarding_steps to detect org admin flows.
 */
export function getOnboardingPathFromAction(
  action: Action,
  programId: number
): string {
  const displayOptions = action.display_options as {
    onboarding_steps?: OnboardingStepDef[];
  } | null;

  const steps = displayOptions?.onboarding_steps ?? [];
  const isOrgAdmin = steps.some((s) => ORG_ONBOARDING_SLUGS.includes(s.slug));

  if (isOrgAdmin) {
    const orgId = (action.target_object_data as { id?: number } | null)?.id;
    if (orgId) {
      return `/program/${programId}/organization/${orgId}/onboarding`;
    }
  }

  return `/program/${programId}/onboarding/participant`;
}

/**
 * Build the correct onboarding URL from invitation type + org ID
 * (for signup/claim redirect where backend provides these fields).
 */
export function getOnboardingPathFromInvitation({
  invitationType,
  organizationId,
  programId,
}: {
  invitationType: string;
  organizationId: number | null;
  programId: number;
}): string {
  if (
    (invitationType === "orgAdmin" || invitationType === "orgAdminPrimary") &&
    organizationId
  ) {
    return `/program/${programId}/organization/${organizationId}/admin/outreach`;
  }

  return `/dashboard/${programId}`;
}
